import type { FC } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import {
  Badge,
  Bleed,
  Box,
  ButtonLink,
  Grid,
  Heading,
  IconList,
  IconLoader,
  Image,
  Stack,
  Stretch,
  Text,
  TextLink,
} from '@eneco/sparky';
import { CheckIcon } from '@eneco/sparky/icons';
import type { BadgeProps, DynamicVariantProp, IconProps } from '@eneco/sparky/types';

import type { TextImageRebrandedContent, TextImageRebrandedUsp } from './text-image-rebranded-content.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RichTextLink: FC<any> = props => <TextLink {...props}>{props.children}</TextLink>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RichTextStrong: FC<any> = props => (
  <Text display="inline" weight="bold">
    {props.children}
  </Text>
);

// `IconLoader` renders `fallbackIcon` as-is, so the colour has to be passed to both.
const uspIconColor: IconProps['color'] = 'feedbackSuccess';

/**
 * `TextImageRebranded` block for the CMS-agnostic lane, serving **both** providers. Text on one side,
 * image on the other, with `imagePosition` deciding which. Mirrors legacy
 * `libs/sitecore/components/content/src/TextImageRebranded/TextImageRebranded.tsx`: its grid, including its
 * reading of `imagePosition` (`right` puts the image on the left from `lg`, and below `lg` the image always
 * comes first), and its text side, whose title sits in a brand-coloured box that overlaps the image's edge,
 * above a padded column with the text, the selling points and the link.
 *
 * As legacy, the title is always an `h2` and is rendered even when empty, and the selling points are those
 * with a title (on Sanity `uspList` is still unresolved, so none show).
 *
 * Known simplification: one link, `buttonLink` or else `link`, where legacy shows both (AB#898735).
 */
const TextImageRebranded: FC = () => {
  const { adapter } = useCms();
  const content = useContent<TextImageRebrandedContent>();
  const mapImage = useImageMapper();

  const objectFit = (useSetting('objectFit') ?? 'cover') as DynamicVariantProp<'objectFit'>;
  const isReversed = useSetting('imagePosition') === 'right';

  const image = mapImage(content.image);
  const richText = adapter.toRichText(content.content);
  const cta = linkWithUrl(content.buttonLink) ?? linkWithUrl(content.link);
  const visibleUspList = (content.uspList ?? []).filter((usp): usp is TextImageRebrandedUsp =>
    Boolean((usp as TextImageRebrandedUsp | null)?.title),
  );

  const gridColumns = {
    left: { initial: '1 / -1', lg: isReversed ? '7 / 13' : '1 / 7', xl: isReversed ? '8 / 13' : '1 / 6' },
    right: { initial: '1 / -1', lg: isReversed ? '1 / 8' : '6 / 13', xl: isReversed ? '1 / 9' : '5 / 13' },
  };

  const imageBlock = image?.src ? (
    <Bleed right={{ initial: '6', md: '10', lg: '0' }}>
      <Image
        alt={image.alt ?? ''}
        aspectRatio="4/3"
        borderRadius={{ initial: ['rounded', 'square', 'square', 'rounded'], lg: 'rounded' }}
        height="100%"
        objectFit={objectFit}
        sources={image.sources}
        src={image.src}
        width="100%"
      />
    </Bleed>
  ) : null;

  const textBlock = (
    <Bleed left={{ initial: '8', md: '12', lg: '0' }} vertical={{ initial: '8', lg: '0' }}>
      <Box paddingTop={{ initial: '0', lg: '4' }}>
        <Grid columns="12">
          <Grid.Item gridColumn={{ initial: '1/13' }}>
            <Stack>
              <Box
                backgroundColor="backgroundBrand"
                borderRadius="l"
                borderBottomLeftRadius={{ initial: 'none', lg: 'l' }}
                borderTopLeftRadius={{ initial: 'none', lg: 'l' }}
                paddingLeft={{ initial: '8', md: '12', lg: '8' }}
                padding="8">
                <Heading as="h2" size="M" color="textInverted">
                  {content.title}
                </Heading>
                {content.badgeText && (
                  <Stack gap="3">
                    <Stack.Item grow={false}>
                      <Badge size="M" color={content.badgeColorOption as BadgeProps['color']}>
                        {content.badgeText}
                      </Badge>
                    </Stack.Item>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Grid.Item>
          <Grid.Item gridColumn={{ initial: '1/13', lg: isReversed ? '3/13' : '1/11' }}>
            <Box
              paddingLeft={{ initial: '8', md: '12', lg: isReversed ? '4' : '8' }}
              padding="8"
              paddingRight={{ initial: '0', lg: '8' }}>
              <Stack gap="6">
                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                  <RichText html={richText} replacements={{ a: RichTextLink, strong: RichTextStrong }} />
                </Text>
                {visibleUspList.length > 0 && (
                  <IconList alignment="top">
                    {visibleUspList.map((usp, index) => (
                      <IconList.Item
                        key={usp._key ?? usp.id ?? index}
                        icon={
                          <IconLoader
                            iconName={usp.iconOption}
                            fallbackIcon={<CheckIcon color={uspIconColor} />}
                            color={uspIconColor}
                          />
                        }>
                        {usp.description ? (
                          <Stack gap="1">
                            <Text weight="bold">{usp.title}</Text>
                            <Text size="BodyS">
                              <RichText html={adapter.toRichText(usp.description)} />
                            </Text>
                          </Stack>
                        ) : (
                          usp.title
                        )}
                      </IconList.Item>
                    ))}
                  </IconList>
                )}
                {cta?.text && (
                  <Stack.Item grow={false}>
                    {/* As legacy: full width below `lg`, so a long label wraps rather than overflowing the column. */}
                    <Stretch width={{ initial: true, lg: false }}>
                      <ButtonLink href={cta.href} target={cta.target}>
                        {cta.text}
                      </ButtonLink>
                    </Stretch>
                  </Stack.Item>
                )}
              </Stack>
            </Box>
          </Grid.Item>
        </Grid>
      </Box>
    </Bleed>
  );

  return (
    <Grid columns="12" gridTemplateRows="1fr" alignY="center" gap={{ initial: '0', lg: '4', xl: '6' }}>
      <Grid.Item gridColumn={gridColumns.left} gridRow={{ lg: '1 / -1' }} order={{ initial: 2, lg: 2 }}>
        {textBlock}
      </Grid.Item>
      <Grid.Item
        gridColumn={gridColumns.right}
        order={{ initial: -1, lg: isReversed ? -1 : 1 }}
        gridRow={{ lg: '1 / -1' }}>
        {imageBlock}
      </Grid.Item>
    </Grid>
  );
};

export default TextImageRebranded;
